import React, { useEffect, useState } from 'react';
import { IAxiosGetResponse, IServiceDto, ISyncType, ITransactionRouteDto, IServiceAggregatorTransactionRouteDto, ITransformationsTransactionDto, 
  ITransactionRoutesDifferenceDto, 
  IAuthenticationType,
  IRequestPayloadType,
  ITransactionMethod,
  IServiceConsumingType} from '../../interfaces/IUtilities';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../modules/layout/components/ToastContext';
import { useNavigate, useParams } from 'react-router-dom';
import { getById, updateById } from '../_request';
import Loader from '../../modules/layout/components/Loader';
import CapitalizeFirstLetter from '../../helpers/Utils';
import ConfirmModal from '../../modules/layout/components/ConfirmModal';
import AppBreadcrumb from '../../modules/layout/components/AppBreadcrumb';
import BackButton from '../../modules/layout/components/BackButton';
import { ArrowClockwise } from 'react-bootstrap-icons';
import { AuthenticationTypeText, RequestPayloadTypeText, ServiceConsumingTypeText, TransactionMethodText } from '../../helpers/EnumeratorHelper';
import { userHasRole } from '../../../setup/auth/authService';
import { Transactions_Approve } from '../../constants/constants';


const serviceSAFields: (keyof IServiceAggregatorTransactionRouteDto)[] = [
  "id", "className", "serviceConsumingType", "transactionMethod", "authenticationType",
  "requestPayloadType", "postLocation", "accessTokenUrl", "retryInterval", "enabled", "operationType",
  "serviceId", "isDeleted", "isApproved"
];

const serviceTRFields: (keyof ITransformationsTransactionDto)[] = [
  "id", "className", "postLocation", "authenticationType", "requestPayloadType", "transactionMethod",  
  "accessTokenUrl", "serviceConsumingType", "serviceId", "isDeleted", "isApproved"
]; //"createdAt", "modifiedAt"

const trFieldKeyMap: Record<keyof ITransformationsTransactionDto, string> = {
  id: "id",
  className: "tr-className",
  postLocation: "tr-postLocation",
  authenticationType: "tr-authenticationType",
  requestPayloadType: "tr-requestPayloadType",
  transactionMethod: "tr-transactionMethod",
  accessTokenUrl: "tr-accessTokenUrl",
  serviceConsumingType: "tr-serviceConsumingType",
  serviceId: "tr-serviceId",
  createdAt: '',
  modifiedAt: '',
  isDeleted: "tr-isDeleted",
  isApproved: "tr-isApproved"
};

const TransactionDataDifferences = () => { 
  const { t } = useTranslation();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const { serviceid, id } = useParams<{ serviceid: string; id: string }>();
  const [service, setService] = useState({} as IServiceDto);
  const [diffSAData, setDiffSAData] = useState({} as ITransactionRoutesDifferenceDto);
  const [diffTRData, setDiffTRData] = useState({} as ITransactionRoutesDifferenceDto);
  const [showModal, setShowModal] = useState(false);
  const [enableSASync, setEnableSASync] = useState(false);  
  const [enableTRSync, setEnableTRSync] = useState(false);  
  const [titleSync, setTitleSync] = useState('');  
  const [syncBtn, setSyncBtn] = useState('');
  const [isApprover, setIsApprover] = useState(false);

  const fetchServiceData = async () => {
      try {
        let result = await getById("/api/services/", serviceid!);
        let respStatus = result?.status || result?.response.status;

        if (respStatus === 200) {
            setService(result.data as IServiceDto);
            setLoading(false);
        }
        else if (respStatus === 403) {
            navigate("/error/403");
        }
        else if (respStatus === 401) {
            navigate("/auth/login");
        }
        else if (respStatus === 500) {
            navigate("/error/500");
        }
        else {
          showToast(t('StatusCode', { code: String(respStatus) }), 'error');
          throw new Error(t('StatusCode', { code: String(respStatus) }));
        }
      } catch (error) {
          setLoading(false);
          console.log(t('ConnectionError', { error: String(error) }));
          showToast(t('ConnectionError', { error: String(error) }), 'error');
          navigate(`/services/${serviceid}/transactions/list`);
      }
  };

  const fetchSAData = async (hasRole: boolean) => {
      try {
        let result = await getById('/api/transactionroutes/comparesatransactions/', id!);
        let respStatus = result?.status || result?.response.status;

        if (respStatus === 200) {
          let data = result.data as ITransactionRoutesDifferenceDto;
          setDiffSAData(data);

          let isTransactionApproved = !data?.adminWebTransaction.isApproved;
          //isTranscationApproved &&
          let saSync = (hasRole && (data?.diffColumnsList?.some(col => !col.startsWith("tr-")) || data?.serviceAggregatorTransaction === null)) ?? false;    
          setEnableSASync(saSync); 
          setLoading(false);
        }
        else if (respStatus === 403) {
          navigate("/error/403");
        }
        else if (respStatus === 401) {
          navigate("/auth/login");
        }
        else if (respStatus === 500) {
          navigate("/error/500");
        }
        else{
          showToast(t('StatusCode', { code: String(respStatus) }), 'error');
          throw new Error(t('StatusCode', { code: String(respStatus) }));
        }
      } catch (error) {
        setLoading(false);
        console.log(t('ConnectionError', { error: String(error) }));
        showToast(t('ConnectionError', { error: String(error) }), 'error');
        navigate(`/services/${serviceid}/transactions/list`);
      }
  };

  const fetchTRData = async (hasRole: boolean) => {
      try {
        let result = await getById('/api/transactionroutes/comparetrtransactions/', id!);
        let respStatus = result?.status || result?.response.status;

        if (respStatus === 200) {
          let data = result.data as ITransactionRoutesDifferenceDto;
          setDiffTRData(data);

          let isTransactionApproved = !data?.adminWebTransaction.isApproved;
          //isTranscationApproved && 
          let trSync = (hasRole && (data?.diffColumnsList?.some(col => col.startsWith("tr-")) || data?.transformationsTransaction === null)) ?? false;
          setEnableTRSync(trSync);
          setLoading(false);
        }
        else if (respStatus === 403) {
          navigate("/error/403");
        }
        else if (respStatus === 401) {
          navigate("/auth/login");
        }
        else if (respStatus === 500) {
          navigate("/error/500");
        }
        else{
          showToast(t('StatusCode', { code: String(respStatus) }), 'error');
          throw new Error(t('StatusCode', { code: String(respStatus) }));
        }
      } catch (error) {
        setLoading(false);
        console.log(t('ConnectionError', { error: String(error) }));
        showToast(t('ConnectionError', { error: String(error) }), 'error');
        navigate(`/services/${serviceid}/transactions/list`);
      }
  };

  useEffect(() => {
    const fetchAll = async () => { 
      await fetchServiceData(); 
      const hasRole = await checkUserRole();
      await Promise.all([fetchSAData(hasRole), fetchTRData(hasRole)]);
    };

    fetchAll();
  }, []);

  const getFormattedValue = (field: string, value: any): string => {
    switch (field) {
      case 'authenticationType': return AuthenticationTypeText[value as IAuthenticationType];
      case 'requestPayloadType': return RequestPayloadTypeText[value as IRequestPayloadType];
      case 'transactionMethod': return TransactionMethodText[value as ITransactionMethod];
      case 'serviceConsumingType': return ServiceConsumingTypeText[value as IServiceConsumingType];
      default: return String(value ?? '');
    }
  };

  const handleSyncSA = () => {
    setShowModal(true);
    setTitleSync(t('SyncIn', { database: "Service Aggregator" }));
    setSyncBtn(ISyncType.ServiceAggregator);
  };

  const handleSyncTR = () => {
    setShowModal(true);
    setTitleSync(t('SyncIn', { database: "Transformations" }));
    setSyncBtn(ISyncType.Transformations);
  };

  const handleConfirmSync = async() => {
    setLoading(true);
    try {
      let result = {} as any;
      let respStatus;

      if (syncBtn === ISyncType.ServiceAggregator) {
        try {
          let resultSAService = await getById('/api/services/checksaservice/', serviceid!);
          let respStatusSAService = resultSAService?.status || resultSAService?.response.status;
  
          if (respStatusSAService === 200) {
            showToast(t('SyncServiceInDatabaseAddSuccess', { database: "Service Aggregator" }), 'success');
          }
          else if (respStatusSAService === 403) {
            navigate("/error/403");
          }
          else if (respStatusSAService === 401) {
            navigate("/auth/login");
          }
          else if (respStatusSAService === 500) {
            navigate("/error/500");
          }
          else{
            showToast(t('StatusCode', { code: String(respStatus) }), 'error');
            throw new Error(t('StatusCode', { code: String(respStatus) }));
          }
        } catch (error) {
          setLoading(false);
          console.log(t('ConnectionError', { error: String(error) }));
          showToast(t('ConnectionError', { error: String(error) }), 'error');
          navigate(`/services/${serviceid}/transactions/list`);
        }
        result = await updateById('/api/transactionroutes/syncsatransactions', id!);
        respStatus = result?.status || result?.response.status;
      }
      else if (syncBtn === ISyncType.Transformations) {
        try {
          let resultTRService = await getById('/api/services/checktrservice/', serviceid!);
          let respStatusTRService = resultTRService?.status || resultTRService?.response.status;
  
          if (respStatusTRService === 200) {
            showToast(t('SyncServiceInDatabaseAddSuccess', { database: "Transformations" }), 'success');
          }
          else if (respStatusTRService === 403) {
            navigate("/error/403");
          }
          else if (respStatusTRService === 401) {
            navigate("/auth/login");
          }
          else if (respStatusTRService === 500) {
            navigate("/error/500");
          }
          else{
            showToast(t('StatusCode', { code: String(respStatusTRService) }), 'error');
            throw new Error(t('StatusCode', { code: String(respStatusTRService) }));
          }
        } catch (error) {
          setLoading(false);
          console.log(t('ConnectionError', { error: String(error) }));
          showToast(t('ConnectionError', { error: String(error) }), 'error');
          navigate(`/services/${serviceid}/transactions/list`);
        }

        result = await updateById('/api/transactionroutes/synctrtransactions', id!);
        respStatus = result?.status || result?.response.status;
      }

      if (respStatus === 200) {
        let isSync = result.data as boolean;
        setLoading(false);

        if (syncBtn === ISyncType.ServiceAggregator) {
          await fetchSAData(isApprover);
        }
        else if (syncBtn === ISyncType.Transformations) {
          await fetchTRData(isApprover);
        }

        if(isSync) {
          if (syncBtn === ISyncType.ServiceAggregator) {
            showToast(t('SyncInDatabaseAddSuccess', { database: "Service Aggregator" }), 'success');
          }
          else {
            showToast(t('SyncInDatabaseAddSuccess', { database: "Transformations" }), 'success');
          }
        }
        else{
          if (syncBtn === ISyncType.ServiceAggregator) {
            showToast(t('SyncInDatabaseAddFail', { database: "Service Aggregator" }), 'error');
          }
          else {
            showToast(t('SyncInDatabaseAddFail', { database: "Transformations" }), 'error');
          }
        }
      }
      else if (respStatus === 400 && result.data?.errors) {
          setLoading(false);
          for (const key in result.data.errors) {
              showToast(t(key), 'error');
          }
      }
      else if (respStatus === 403) {
        navigate("/error/403");
      }
      else if (respStatus === 401) {
        navigate("/auth/login");
      }
      else if (respStatus === 500) {
        navigate("/error/500");
      }
      else {
        showToast(t('StatusCode', { code: String(respStatus) }), 'error');
        throw new Error(t('StatusCode', { code: String(respStatus) }));
      }
    } catch (error) {
      setLoading(false);
      console.log(t('ConnectionError', { error: String(error) }));
      showToast(t('ConnectionError', { error: String(error) }), 'error');
      navigate(`/services/${serviceid}/transactions/list`);
    }

    setShowModal(false);
  };
  
  const handleCancelSync = () => {
    setShowModal(false);
  };

  const handleRefreshClick = async (btnName: string) => {
    setLoading(true);
    try {
      if (btnName === ISyncType.ServiceAggregator) {
        await fetchSAData(isApprover);
        showToast(t('DatabaseTableRefreshed', { database: "Service Aggregator" }), 'success');
      }
      else if (btnName === ISyncType.Transformations) {
        await fetchTRData(isApprover);
        showToast(t('DatabaseTableRefreshed', { database: "Transformations" }), 'success');
      }
    } catch (error) {
      setLoading(false);
      console.log(t('RefreshError'), error);
      showToast(`${t('FailedToRefresh')} ${btnName === ISyncType.ServiceAggregator ? 'Service Aggregator' : 'Transformations'} ${t('Table')}`, 'error');
    }
    setLoading(false);
  };

  const checkUserRole = async (): Promise<boolean> => {
    const hasRole = await userHasRole(Transactions_Approve);
    setIsApprover(hasRole);
    return hasRole;
  }


  return (
    <div className="container mt-4 gg-transdiff">
      <AppBreadcrumb 
        items={[
            { title: t('Home'), href: "/" },
            { title: t('Services'), href: "/services/list" },
            { title: `${service ? service.name : ""}: ${t('TransactionRoutes')}`, href: `/services/${serviceid}/transactions/list` },
            { title: `${diffSAData.adminWebTransaction ? diffSAData.adminWebTransaction?.routeLabel : ""}: ${t('TransactionRoutesDataDifference')}` } // Active page (no href)
        ]} />
        
      <h3>{t('TransactionRoutesDataDifferences')}</h3>

      {loading && (<Loader />)}

      {(diffSAData || diffTRData) ?
        <div className="gg-trans-diff">
          <h5>{t('RecordsFromDatabases')}</h5>

            <div key={diffSAData.id} className="my-3 border rounded p-3 shadow">
              <h4 className="text-start font-bold mb-2">{t('TransactionsRouteID')}: {diffSAData.id}</h4>

              <div className="row gg-trans-sa-diff">
                <div className="col">
                  <div className="table-responsive">
                    <table className="table table-bordered text-start w-full">
                      <thead>
                        <tr>
                          <th className="p-1 text-left">{t('Source')}</th>
                          {serviceSAFields.map((field) => (
                            <th key={field} className="p-1 text-left">
                              <CapitalizeFirstLetter text={field} />
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {['adminWebTransaction', 'serviceAggregatorTransaction'].map((sourceKey) => {
                          const sourceData =
                            sourceKey === 'adminWebTransaction'
                              ? (diffSAData.adminWebTransaction as ITransactionRouteDto)
                              : (diffSAData.serviceAggregatorTransaction as IServiceAggregatorTransactionRouteDto);

                          return (
                            <tr key={`${diffSAData.id}-${sourceKey}`}>
                              <td className={`p-1 border font-semibold ${diffSAData.diffColumnsList?.some(col => !col.startsWith("tr-")) ? 'text-danger' : ''}`}>
                                {sourceKey === 'adminWebTransaction' ? 'Admin Web' : 'Service Aggregator'} {t('Database')}
                              </td>

                              {serviceSAFields.map((field) => {
                                let value = getFormattedValue(field, sourceData?.[field as keyof typeof sourceData]);
                                const isDifferent = diffSAData.diffColumnsList?.includes(field);
                                value = (value === null || value === undefined) ? "-" : value;
                                
                                switch (diffSAData.differenceType) {
                                  case 0:
                                    return (
                                      <td
                                        key={field}
                                        className="p-1 border text-success"
                                      >
                                        {String(value)}
                                      </td> 
                                    );

                                  case 1:
                                    return diffSAData.serviceAggregatorTransaction ? (
                                      <td
                                        key={field}
                                        className={`p-1 border ${isDifferent ? 'text-danger fw-bold' : 'text-success'}`}
                                      >
                                        {value}
                                      </td>
                                    ) : <td
                                        key={field}
                                        className={`p-1 border ${sourceKey === 'adminWebTransaction' ? 'text-primary' : ''}`}
                                      >
                                        {sourceKey === 'serviceAggregatorTransaction' ? '-' : value}
                                      </td>;

                                  default:
                                    return (
                                      <td
                                        key={field}
                                        className="p-1 border text-primary"
                                      >
                                        {'-'}
                                      </td> 
                                    );
                                }

                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="row my-2">
                <div className="col d-flex justify-content-end align-items-end">
                  <button title={t('Refresh')} className="btn btn-success me-2 gg-btn-refresh" onClick={() => handleRefreshClick(ISyncType.ServiceAggregator)}>
                    <ArrowClockwise size={20}/>
                  </button>
                  <button type="button" disabled={!enableSASync} className="btn btn-warning"
                    onClick={handleSyncSA}>{t('SyncIn', { database: "Service Aggregator" })}</button>
                </div>
              </div>

              <div className="row gg-trans-tr-diff">
                <div className="col">
                  <div className="table-responsive">
                    <table className="table table-bordered text-start w-full">
                      <thead>
                        <tr>
                          <th className="p-1 text-left">{t('Source')}</th>
                          {serviceTRFields.map((field) => (
                            <th key={field} className="p-1 text-left">
                              <CapitalizeFirstLetter text={field} />
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {['adminWebTransaction', 'transformationsTransaction'].map((sourceKey) => {
                          const sourceData =
                            sourceKey === 'adminWebTransaction'
                              ? (diffTRData.adminWebTransaction as ITransactionRouteDto)
                              : (diffTRData.transformationsTransaction as ITransformationsTransactionDto);

                          return (
                            <tr key={`${diffTRData.id}-${sourceKey}`}>
                              <td className={`p-1 border font-semibold ${diffTRData.diffColumnsList?.some(col => col.startsWith("tr-")) ? 'text-danger' : ''}`}>
                                {sourceKey === 'adminWebTransaction' ? 'Admin Web' : 'Transformations'} {t('Database')}
                              </td>

                              {serviceTRFields.map((field) => {
                                const backendKey = trFieldKeyMap[field];
                                let value = getFormattedValue(field, sourceData?.[field as keyof typeof sourceData]);
                                const isDifferent = diffTRData.diffColumnsList?.includes(backendKey);
                                value = (value === null || value === undefined) ? "-" : value;
                                
                                switch (diffTRData.differenceType) {
                                  case 0:
                                    return (
                                      <td
                                        key={field}
                                        className="p-1 border text-success"
                                      >
                                        {String(value)}
                                      </td> 
                                    );

                                  case 1:
                                    return diffTRData.transformationsTransaction ? (
                                      <td
                                        key={field}
                                        className={`p-1 border ${isDifferent ? 'text-danger fw-bold' : 'text-success'}`}
                                      >
                                        {value}
                                      </td>
                                    ) : <td
                                        key={field}
                                        className={`p-1 border ${sourceKey === 'adminWebTransaction' ? 'text-primary' : ''}`}
                                      >
                                        {sourceKey === 'transformationsTransaction' ? '-' : value}
                                      </td>;

                                  default:
                                    return (
                                      <td
                                        key={field}
                                        className="p-1 border text-primary"
                                      >
                                        {'-'}
                                      </td> 
                                    );
                                }

                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="row my-2">
                <div className="col d-flex justify-content-end align-items-end">
                  <button title={t('Refresh')} className="btn btn-success me-2 gg-btn-refresh" onClick={() => handleRefreshClick(ISyncType.Transformations)}>
                    <ArrowClockwise size={20}/>
                  </button>
                  <button type="button" disabled={!enableTRSync} className="btn btn-warning"
                    onClick={handleSyncTR}>{t('SyncIn', { database: "Transformations" })}</button>
                </div>
              </div>

            </div>
        </div>
      : <p className="text-warning">{t('NoDataDifferencesFoundBetweenAWAndDatabases')}</p>}

      <ConfirmModal
        show={showModal}
        title={titleSync}
        message={t('SyncWithDatabase')}
        onConfirm={handleConfirmSync}
        onCancel={handleCancelSync}
        buttonClass="warning"
        loading={loading}
      />
      
      <div className="row">
        <div className="col d-flex justify-content-between align-items-end">
          <BackButton goTo={`/services/${serviceid}/transactions/list`} />
        </div>
      </div>

    </div>
  );
};

export default TransactionDataDifferences;
